import ExampleTheme from "./themes/ExampleTheme";
import { $createParagraphNode, $createTextNode, $getRoot, $insertNodes, $getSelection, $isRangeSelection } from "lexical";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import TreeViewPlugin from "./plugins/TreeViewPlugin";
import ToolbarPlugin from "./plugins/ToolbarPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TRANSFORMERS } from "@lexical/markdown";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { useEffect, useState } from "react";
import {OnChangePlugin} from '@lexical/react/LexicalOnChangePlugin';
import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin";
import AutoLinkPlugin from "./plugins/AutoLinkPlugin";
import { Button, ButtonGroup} from '@mui/material';




function UpdatePlugin(btns) {
  const [editor] = useLexicalComposerContext();
  console.log("btns: ",btns);
  console.log("btns.btns: ",btns.btns);
   console.log("btns.scaffoldBody: ",btns.scaffoldBody);
  function update(text) {
    editor.update(() => {
      const selection = $getSelection();
      const root = $getRoot();
      
      // 如果有選擇，在選擇位置插入內容
      if ($isRangeSelection(selection)) {
        const textNode = $createTextNode(text);
        selection.insertNodes([textNode]);
      } else {
        // 如果沒有選擇，在文檔末尾追加內容
        const lastChild = root.getLastChild();
        
        // 如果最後一個子節點是段落，在段落末尾追加
        if (lastChild && lastChild.getType() === 'paragraph') {
          const textNode = $createTextNode(text);
          lastChild.append(textNode);
        } else {
          // 如果沒有段落，創建新段落並添加文本
          const paragraph = $createParagraphNode();
          const textNode = $createTextNode(text);
          paragraph.append(textNode);
          root.append(paragraph);
        }
      }
    });
  }
  return <>
    {
       (btns?.scaffoldBody || btns?.btns?.scaffoldBody)?.map((text,index)=>{
        return <Button
          key={index}
          onClick={() => {
            console.log(text);
            update(text);
            
          }}
          sx={{
            width: '100% !important',
            margin: '2px 0 !important',
            padding: '12px 16px !important',
            fontSize: '14px !important',
            textTransform: 'none !important',
            backgroundColor: '#ffffff !important',
            color: '#1e4d5f !important',
            border: '1px solid #87ceeb !important',
            borderRadius: '8px !important',
            fontWeight: '500 !important',
            cursor: 'pointer !important',
            transition: 'all 0.2s ease !important',
            display: 'flex !important',
            alignItems: 'center !important',
            justifyContent: 'flex-start !important',
            textAlign: 'left !important',
            minWidth: 'auto !important',
            '&:hover': {
              backgroundColor: '#f0f8ff !important',
              borderColor: '#1e4d5f !important',
              transform: 'translateY(-1px) !important',
              boxShadow: '0 2px 8px rgba(30, 77, 95, 0.15) !important'
            },
            '&:active': {
              transform: 'translateY(0) !important',
              boxShadow: '0 1px 4px rgba(30, 77, 95, 0.1) !important'
            }
          }}
        >
          {text}
        </Button>;
      })
    }
  </>
};

function Placeholder({ wallType, nodeType }) {
  // 根據牆面類型和節點類型顯示不同的引導文字
  const getPlaceholderText = () => {
    if (wallType === 'question') {
      if (nodeType === 'question') {
        return '請輸入你的問題...';
      } else if (nodeType === 'reply') {
        return '請輸入你的回覆...';
      }
      return '請輸入你的想法...';
    } else if (wallType === 'idea') {
      return '請輸入你的想法...';
    }
    return '請輸入內容...';
  };

  return <div className="editor-placeholder">{getPlaceholderText()}</div>;
}

const HtmlPlugin = ({ initialHtml, onHtmlChanged, shouldUseInitialHtml }) => {
  const [editor] = useLexicalComposerContext();

  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
      // 只在第一次渲染且 shouldUseInitialHtml 為 true 時才插入初始內容
      if (!initialHtml || !isFirstRender || !shouldUseInitialHtml) return;

      setIsFirstRender(false);

      editor.update(() => {
          const parser = new DOMParser();
          const dom = parser.parseFromString(initialHtml, "text/html");
          const nodes = $generateNodesFromDOM(editor, dom);
          $insertNodes(nodes);
      });
  }, [initialHtml, isFirstRender, shouldUseInitialHtml, editor]);

  return (
      <OnChangePlugin
          onChange={(editorState) => {
              editorState.read(() => {
                  onHtmlChanged($generateHtmlFromNodes(editor));
              });
          }}
      />
  );
};

const editorConfig = {
  // The editor theme
  theme: ExampleTheme,
  // Handling of errors during update
  onError(error) {
    throw error;
  },
  // Any custom nodes go here
  nodes: [
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    CodeNode,
    CodeHighlightNode,
    TableNode,
    TableCellNode,
    TableRowNode,
    AutoLinkNode,
    LinkNode
  ]
};

export default function LexicalEditor({ changeFunc, nodePlan }) {
  // 從 nodePlan 中獲取牆面類型和節點類型
  const wallType = nodePlan?.wall_type || 'idea';
  const nodeType = nodePlan?.tags || nodePlan?.nodeType || 'idea';
  
  // 決定是否使用初始 HTML（目前不使用，改用 placeholder）
  const shouldUseInitialHtml = false;

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <ButtonGroup
            orientation="vertical"
            aria-label="vertical outlined button group"
            style={{ color:'#ECF2FF' }}
          >
              <UpdatePlugin
                btns={nodePlan} 
              />
                     
      </ButtonGroup>
      <div className="editor-container">
        <ToolbarPlugin />
        <div className="editor-inner">
          <RichTextPlugin
            contentEditable={<ContentEditable className="editor-input" />}
            placeholder={<Placeholder wallType={wallType} nodeType={nodeType} />}
          />
          <HistoryPlugin />
          {/* <TreeViewPlugin /> */}
          <AutoFocusPlugin />
          <CodeHighlightPlugin />
          <ListPlugin />
          <LinkPlugin />
          <AutoLinkPlugin />
          <ListMaxIndentLevelPlugin maxDepth={7} />

          <HtmlPlugin
            onHtmlChanged={(html) => changeFunc(html)}
            initialHtml=""
            shouldUseInitialHtml={shouldUseInitialHtml}
          />
        </div>
      </div>
    </LexicalComposer>
  );
}
